/* DrinksNation shopping assistant (S6-13, 18 Sep 2026). Loaded by sections/dn-assistant.liquid and nothing else.
   Text only: no voice, no microphone, nothing leaves the page but what the shopper types (decided 17 Sep 2026).

   What it must do, in the order it matters:
   1. Nothing shows while the Brain is down, switched off or resting for the month. The launcher is `hidden` on the
      server and appears only after GET /apps/dn/assistant answers 200 {available: true}. Any other answer - 503,
      a timeout, HTML, JS off - leaves the page exactly as it was, with nothing logged. The answer is remembered for
      ten minutes in sessionStorage so each page view is not a request.
   2. Nothing goes into the basket without a tap. A reply may carry proposals; each line shows the product, the pack,
      the GST-inclusive carton price, the container deposit when there is one (its own line, never folded into the
      price) and an Add button. Only a tap adds, through the basket's own add (dn-cart.js,
      which then settles the deposit and fee lines), or /cart/add.js when that is not on the page.
   3. It never shifts layout. Launcher and panel are position: fixed from their first paint.
   4. The panel is a dialog: focus moves into it, Tab stays inside it, Escape closes it and focus returns to the
      launcher. Reduced motion is respected in dn-assistant.css.

   The reply streams as newline-delimited JSON events (text, status, proposal, error, done); a body that arrives all
   at once is read the same way. The browser keeps the conversation (a random id and the text turns) in
   sessionStorage; the Brain keeps none of it.

   Trust: everything from the Brain is written with textContent. Product links are built from a handle that must
   match [a-z0-9-]. Vanilla JS, no framework. Pure helpers are exported for node: qa/unit/dn-assistant.test.mjs. */
(function (root, factory) {
  var api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.dnAssistant = api;
  if (typeof document !== 'undefined' && typeof fetch === 'function') api.boot();
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  var STATUS_TTL_MS = 10 * 60 * 1000;
  var STATUS_TIMEOUT_MS = 4000;
  var REPLY_TIMEOUT_MS = 90000;
  var MAX_HISTORY = 16;
  var MAX_CHARS = 500;
  var STATUS_KEY = 'dn-assistant-status';
  var CHAT_KEY = 'dn-assistant-chat';
  var HIDE_REASONS = { not_configured: 1, over_ceiling: 1, unavailable: 1 };
  var MESSAGES = {
    over_ceiling: 'The assistant is resting for the month. Search and the product pages work as usual.',
    unavailable: 'The assistant cannot answer right now. Search and the product pages work as usual.',
    not_configured: 'The assistant cannot answer right now. Search and the product pages work as usual.',
    rate_limited: 'That was a lot of messages at once. Try again in a minute.',
    conversation_cap: 'This chat has reached its limit. Start a new chat to carry on.',
    bad_request: 'That message could not be read. Try saying it another way.'
  };
  var TOOL_STATUS = {
    search_products: 'Looking through the shop',
    get_product: 'Reading the product',
    quote_basket: 'Working out the price',
    propose_cart_lines: 'Getting the lines ready to add'
  };

  /* ---------- pure helpers ---------- */

  /** Splits a streamed body into JSON events; a line cut across chunks waits for the rest. Bad lines are skipped. */
  function lineReader() {
    var buf = '';
    function parse(line) {
      line = line.trim();
      if (!line) return null;
      try { var v = JSON.parse(line); return v && typeof v === 'object' ? v : null; } catch (e) { return null; }
    }
    return {
      push: function (chunk) {
        buf += chunk;
        var out = [];
        var i;
        while ((i = buf.indexOf('\n')) > -1) {
          var ev = parse(buf.slice(0, i));
          buf = buf.slice(i + 1);
          if (ev) out.push(ev);
        }
        return out;
      },
      end: function () { var ev = parse(buf); buf = ''; return ev ? [ev] : []; }
    };
  }

  /** Integer cents to "S$12.50". Anything else to ''. */
  function money(cents) {
    if (typeof cents !== 'number' || !isFinite(cents) || cents < 0 || Math.floor(cents) !== cents) return '';
    var s = String(cents % 100);
    return 'S$' + Math.floor(cents / 100) + '.' + (s.length < 2 ? '0' + s : s);
  }

  /** A proposal from the Brain, checked field by field. Lines that do not check out are dropped. */
  function proposalLines(p) {
    if (!p || typeof p !== 'object' || !Array.isArray(p.lines)) return [];
    var out = [];
    p.lines.forEach(function (l) {
      if (!l || typeof l !== 'object') return;
      var id = Number(l.variant_id), q = Number(l.quantity), price = l.unit_price_cents;
      if (!(id > 0) || Math.floor(id) !== id || !(q >= 1 && q <= 999) || Math.floor(q) !== q || !money(price)) return;
      var title = typeof l.title === 'string' ? l.title.trim() : '';
      if (!title) return;
      out.push({
        variantId: id,
        quantity: q,
        title: title,
        variantTitle: typeof l.variant_title === 'string' ? l.variant_title : '',
        pack: typeof l.pack === 'string' ? l.pack : '',
        price: money(price),
        lineTotal: money(l.line_cents),
        deposit: typeof l.deposit_cents === 'number' && l.deposit_cents > 0 ? money(l.deposit_cents) : '',
        alcohol: l.alcohol === true,
        handle: typeof l.handle === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(l.handle) ? l.handle : ''
      });
    });
    return out;
  }

  /** What a refusal means for the panel: its message, and whether the launcher should go. */
  function refusal(status, body) {
    var reason = body && typeof body.reason === 'string' ? body.reason : (status === 429 ? 'rate_limited' : 'unavailable');
    if (!MESSAGES[reason]) reason = 'unavailable';
    return { reason: reason, hide: !!HIDE_REASONS[reason], message: MESSAGES[reason] };
  }

  /** The text turns the browser sends back: user and assistant alternating, newest kept. */
  function trimHistory(history) {
    var h = (history || []).filter(function (e) { return e && (e.role === 'user' || e.role === 'assistant') && typeof e.text === 'string' && e.text.trim(); });
    h = h.slice(-MAX_HISTORY);
    if (h.length && h[0].role !== 'user') h = h.slice(1);
    return h;
  }

  /** A random v4 UUID from 16 random bytes. */
  function uuid4(bytes) {
    var b = Array.prototype.slice.call(bytes, 0, 16);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    var h = b.map(function (x) { return (x + 0x100).toString(16).slice(1); }).join('');
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
  }

  function newId() {
    var c = root.crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    var bytes = new Uint8Array(16);
    if (c && c.getRandomValues) c.getRandomValues(bytes); else for (var i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    return uuid4(bytes);
  }

  /* ---------- storage, never trusted to exist ---------- */

  function load(key) {
    try { var v = root.sessionStorage.getItem(key); return v ? JSON.parse(v) : null; } catch (e) { return null; }
  }
  function save(key, value) {
    try { root.sessionStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode: fine */ }
  }

  /* ---------- the page ---------- */

  function boot() {
    function start() {
      var el = document.querySelector('[data-dn-assistant]');
      if (!el) return;
      var endpoint = el.getAttribute('data-endpoint');
      var launch = el.querySelector('[data-assistant-open]');
      var panel = el.querySelector('[data-assistant-panel]');
      var log = el.querySelector('[data-assistant-log]');
      var statusLine = el.querySelector('[data-assistant-status]');
      var form = el.querySelector('[data-assistant-form]');
      var input = el.querySelector('[data-assistant-input]');
      var send = el.querySelector('[data-assistant-send]');
      if (!endpoint || !launch || !panel || !log || !form || !input || !send) return;

      var chat = load(CHAT_KEY);
      if (!chat || typeof chat.id !== 'string') chat = { id: newId(), history: [] };
      chat.history = trimHistory(chat.history);
      var busy = false;
      var restored = false;

      function hideAll() {
        launch.hidden = true;
        save(STATUS_KEY, { ok: false, at: Date.now() });
      }

      function bubble(role, text) {
        var p = document.createElement('p');
        p.className = 'dn-assistant__msg dn-assistant__msg--' + (role === 'user' ? 'you' : 'shop');
        var who = document.createElement('span');
        who.className = 'visually-hidden';
        who.textContent = role === 'user' ? 'You: ' : 'Assistant: ';
        var body = document.createElement('span');
        body.textContent = text || '';
        p.appendChild(who);
        p.appendChild(body);
        log.appendChild(p);
        log.scrollTop = log.scrollHeight;
        return body;
      }

      function note(text) {
        var p = document.createElement('p');
        p.className = 'dn-assistant__notice';
        p.textContent = text;
        log.appendChild(p);
        log.scrollTop = log.scrollHeight;
      }

      function addLine(line, button) {
        button.disabled = true;
        button.textContent = 'Adding';
        var done = function (ok) {
          button.textContent = ok ? 'Added' : 'Add';
          button.disabled = !!ok;
          statusLine.textContent = ok ? 'Added ' + line.quantity + ' x ' + line.title + ' to your basket.' : 'That did not go in. Try again.';
        };
        if (root.dnCart && typeof root.dnCart.addItem === 'function') {
          root.dnCart.addItem(line.variantId, line.quantity).then(done, function () { done(false); });
          return;
        }
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: [{ id: line.variantId, quantity: line.quantity }] })
        }).then(function (r) {
          done(r.ok);
          if (r.ok) { try { document.dispatchEvent(new CustomEvent('dn:cart:refresh')); } catch (e) { /* old browser */ } }
        }, function () { done(false); });
      }

      function proposal(p) {
        var lines = proposalLines(p);
        if (!lines.length) return;
        var box = document.createElement('div');
        box.className = 'dn-assistant__proposal';
        var head = document.createElement('p');
        head.className = 'dn-assistant__proposal-head';
        head.textContent = 'Tap Add to put a line in your basket. Prices include GST, before any bulk discount.';
        box.appendChild(head);
        var list = document.createElement('ul');
        list.className = 'dn-assistant__lines';
        lines.forEach(function (line) {
          var li = document.createElement('li');
          li.className = 'dn-assistant__line';
          var text = document.createElement('div');
          text.className = 'dn-assistant__line-text';
          var name = document.createElement(line.handle ? 'a' : 'span');
          name.className = 'dn-assistant__line-name';
          if (line.handle) name.setAttribute('href', '/products/' + line.handle);
          name.textContent = line.title + (line.variantTitle ? ', ' + line.variantTitle : '');
          var meta = document.createElement('span');
          meta.className = 'dn-assistant__line-meta';
          meta.textContent = (line.pack ? line.pack + '. ' : '') + line.quantity + ' x ' + line.price + ' a carton' + (line.lineTotal ? ' = ' + line.lineTotal : '') + '.' +
            (line.deposit ? ' Plus ' + line.deposit + ' container deposit, its own line in the basket.' : '');
          text.appendChild(name);
          text.appendChild(meta);
          if (line.alcohol) {
            var age = document.createElement('span');
            age.className = 'dn-assistant__line-age';
            age.textContent = 'Alcohol, 18 and over. You confirm your age in the basket.';
            text.appendChild(age);
          }
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'dn-assistant__add';
          btn.textContent = 'Add';
          btn.setAttribute('aria-label', 'Add ' + line.quantity + ' x ' + line.title + ' to basket');
          btn.addEventListener('click', function () { addLine(line, btn); });
          li.appendChild(text);
          li.appendChild(btn);
          list.appendChild(li);
        });
        box.appendChild(list);
        log.appendChild(box);
        log.scrollTop = log.scrollHeight;
      }

      function setBusy(on) {
        busy = on;
        send.disabled = on;
        input.setAttribute('aria-busy', on ? 'true' : 'false');
        if (!on) statusLine.textContent = '';
      }

      function ask(text) {
        setBusy(true);
        bubble('user', text);
        var reply = null;
        var replyText = '';
        var stop = typeof AbortController === 'function' ? new AbortController() : null;
        var timer = setTimeout(function () { if (stop) stop.abort(); }, REPLY_TIMEOUT_MS);
        var handle = function (ev) {
          if (ev.type === 'text' && typeof ev.text === 'string') {
            if (!reply) reply = bubble('assistant', '');
            replyText += ev.text;
            reply.textContent = replyText;
            log.scrollTop = log.scrollHeight;
          } else if (ev.type === 'status') {
            statusLine.textContent = (TOOL_STATUS[ev.tool] || 'Working on it') + '.';
          } else if (ev.type === 'proposal') {
            proposal(ev.proposal);
          } else if (ev.type === 'error') {
            note(MESSAGES.unavailable);
          }
        };
        var finish = function () {
          clearTimeout(timer);
          setBusy(false);
          if (replyText.trim()) {
            chat.history = trimHistory(chat.history.concat([{ role: 'user', text: text }, { role: 'assistant', text: replyText.trim() }]));
            save(CHAT_KEY, chat);
          }
          input.focus();
        };
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/x-ndjson, application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ conversation_id: chat.id, message: text, history: chat.history }),
          signal: stop ? stop.signal : undefined
        }).then(function (r) {
          var type = r.headers.get('content-type') || '';
          if (!r.ok || type.indexOf('application/x-ndjson') === -1) {
            return r.json().catch(function () { return null; }).then(function (body) {
              var f = refusal(r.status, body);
              note(f.message);
              if (f.hide) hideAll();
            });
          }
          var lines = lineReader();
          if (!r.body || typeof r.body.getReader !== 'function') {
            return r.text().then(function (t) { lines.push(t).concat(lines.end()).forEach(handle); });
          }
          var reader = r.body.getReader();
          var utf8 = new TextDecoder();
          var pump = function () {
            return reader.read().then(function (step) {
              if (step.done) { lines.push(utf8.decode()).concat(lines.end()).forEach(handle); return; }
              lines.push(utf8.decode(step.value, { stream: true })).forEach(handle);
              return pump();
            });
          };
          return pump();
        }).catch(function () {
          note(MESSAGES.unavailable);
        }).then(finish, finish);
      }

      function restore() {
        if (restored) return;
        restored = true;
        chat.history.forEach(function (e) { bubble(e.role, e.text); });
      }

      /* Focus stays in the open panel: Tab from the last control goes to the first and back. */
      function focusables() {
        return Array.prototype.filter.call(
          panel.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
          function (n) { return !n.hidden && n.offsetParent !== null; }
        );
      }
      function onKey(e) {
        if (e.key === 'Escape') { e.preventDefault(); close(); return; }
        if (e.key !== 'Tab') return;
        var f = focusables();
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        else if (!panel.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      }
      function open() {
        restore();
        panel.hidden = false;
        launch.setAttribute('aria-expanded', 'true');
        el.classList.add('is-open');
        document.addEventListener('keydown', onKey);
        requestAnimationFrame(function () { panel.classList.add('is-in'); input.focus(); });
      }
      function close() {
        panel.classList.remove('is-in');
        panel.hidden = true;
        el.classList.remove('is-open');
        launch.setAttribute('aria-expanded', 'false');
        document.removeEventListener('keydown', onKey);
        if (!launch.hidden) launch.focus();
      }

      launch.addEventListener('click', open);
      el.querySelectorAll('[data-assistant-close]').forEach(function (b) { b.addEventListener('click', close); });
      var fresh = el.querySelector('[data-assistant-new]');
      if (fresh) fresh.addEventListener('click', function () {
        if (busy) return;
        chat = { id: newId(), history: [] };
        save(CHAT_KEY, chat);
        while (log.firstChild) log.removeChild(log.firstChild);
        statusLine.textContent = 'New chat started.';
        input.focus();
      });
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (busy) return;
        var text = input.value.replace(/\s+/g, ' ').trim().slice(0, MAX_CHARS);
        if (!text) return;
        input.value = '';
        ask(text);
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) { e.preventDefault(); form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true })); }
      });

      /* Show the launcher only when the Brain says the assistant is up. */
      var cached = load(STATUS_KEY);
      if (cached && typeof cached.at === 'number' && Date.now() - cached.at < STATUS_TTL_MS) {
        if (cached.ok) launch.hidden = false;
        return;
      }
      var stop = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = setTimeout(function () { if (stop) stop.abort(); }, STATUS_TIMEOUT_MS);
      fetch(endpoint, { headers: { Accept: 'application/json' }, credentials: 'same-origin', signal: stop ? stop.signal : undefined })
        .then(function (r) {
          if (!r.ok || (r.headers.get('content-type') || '').indexOf('application/json') === -1) return null;
          return r.json();
        })
        .then(function (body) {
          var ok = !!(body && body.available === true);
          save(STATUS_KEY, { ok: ok, at: Date.now() });
          launch.hidden = !ok;
        })
        .catch(function () { /* the Brain is down: the page stays as it was */ })
        .then(function () { clearTimeout(timer); });
    }

    var idle = root.requestIdleCallback || function (fn) { return setTimeout(fn, 200); };
    if (document.readyState === 'complete') idle(start);
    else root.addEventListener('load', function () { idle(start); });
  }

  return { lineReader: lineReader, money: money, proposalLines: proposalLines, refusal: refusal, trimHistory: trimHistory, uuid4: uuid4, boot: boot };
});
